import { useEffect, useRef, type ReactNode } from "react";
import { trackEvent, AnalyticsEvent } from "@/analytics";

/**
 * Section-engagement instrumentation for the SOP report (RES-43). The report
 * renders every section open (no accordions), so "which sections do chiefs
 * actually read?" is answered by viewport visibility: fire `sop_section_viewed`
 * ONCE per mount when the section is meaningfully on screen — ≥ 40% of the
 * section visible, or (for sections taller than the screen) the section
 * covering ≥ 60% of the viewport.
 *
 * Renders a plain wrapper div, so each section stays a direct child of
 * `#sop-report` and the print `break-inside: avoid` rule keeps applying
 * per-section. No-op where IntersectionObserver is missing (jsdom, old
 * browsers) and, via `trackEvent`, outside a chief session.
 */

const SECTION_RATIO = 0.4;
const VIEWPORT_RATIO = 0.6;

export function TrackedSection({
  name,
  children,
}: {
  name: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const viewportCoverage =
            entry.intersectionRect.height / Math.max(1, window.innerHeight);
          if (
            entry.intersectionRatio >= SECTION_RATIO ||
            viewportCoverage >= VIEWPORT_RATIO
          ) {
            trackEvent(AnalyticsEvent.SopSectionViewed, { section: name });
            observer.disconnect();
            return;
          }
        }
      },
      { threshold: [0, SECTION_RATIO] },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [name]);
  return <div ref={ref}>{children}</div>;
}
